import {
  OFFER,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  isSoftBookingProcess,
  resolveLatestProcessName,
} from '../transactions/transaction';

export const SOFT_RESERVATION_STATUS_SETUP_PENDING = 'setup-pending';
export const SOFT_RESERVATION_SETUP_DEADLINE_HOURS = 24;

const formatDateMaybe = value => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toISOString();
};

const normalizeBookingDates = orderData => {
  const nested = orderData?.bookingDates;
  if (nested?.bookingStart && nested?.bookingEnd) {
    return nested;
  }
  if (nested?.startDate && nested?.endDate) {
    return {
      bookingStart: nested.startDate,
      bookingEnd: nested.endDate,
    };
  }
  if (orderData?.bookingStart && orderData?.bookingEnd) {
    return {
      bookingStart: orderData.bookingStart,
      bookingEnd: orderData.bookingEnd,
    };
  }
  return null;
};

/**
 * Which of cowork24-soft-booking's two tracks a listing belongs on.
 *
 * Keyed on the PROCESS, not on unitType, and deliberately so: the process decides which
 * reservation action is even valid (create-proposed-stock-reservation needs stock,
 * create-proposed-booking needs a window), and requiresSoftReservationFallback already routes on
 * the same fact. Deciding the track on a second, independent classifier let the two disagree — a
 * default-purchase listing whose unitType was changed in the Console would have been routed into
 * soft-booking and then handled as a booking, failing on `new Date(undefined)` after the card was
 * already saved.
 *
 * Pricing is the one place that legitimately still keys on unitType: server/api-util/lineItems.js
 * picks line-item/item from it, and that is its own contract.
 */
export const usesStockTrack = listing => {
  const alias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  return isPurchaseProcess(resolveLatestProcessName(alias.split('/')[0]));
};

export const requiresSoftReservationFallback = listing => {
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const unitType = listing?.attributes?.publicData?.unitType;
  // The purchase process is back in, and cowork24-soft-booking now carries a second track for it:
  // request-soft-purchase reserves stock instead of creating a booking. Before that track existed
  // this line sent 13 prod listings into a booking-only process with no dates to give, and every
  // attempt ended on "Diese Buchung ist nicht mehr verfuegbar".
  //
  // What it does NOT do is make those providers payable: accept-and-charge-item still runs
  // stripe-capture-payment-intent, whose precondition is a connected Stripe account. The value is
  // that the request now REACHES the provider — the request email carries the "Stripe-Konto
  // erforderlich" banner with a direct link — instead of dead-ending in front of the customer.
  const isPaymentProcess =
    isBookingProcess(processName) ||
    isPurchaseProcess(processName) ||
    (isNegotiationProcess(processName) && unitType === OFFER);
  // currentUser.attributes.stripeConnected is private and not available on included
  // author entities via the Marketplace API. We mirror it into profile.publicData
  // (see stripeConnectAccount.duck.js / user.duck.js) so it is readable by anyone.
  const authorPublicData = listing?.author?.attributes?.profile?.publicData;

  // Two states must both be true before a provider can accept normal Stripe checkout:
  //   1. Stripe Connect account exists (provider completed OAuth onboarding), AND
  //   2. Stripe has marked the account as verified (charges_enabled === true).
  // Both conditions are encoded in profile.publicData.stripeConnected, which is written
  // by syncStripeStatus from Stripe's charges_enabled flag — that flag is true only
  // after onboarding AND identity verification are complete.
  // We require an explicit `true`. Anything else (false, null, undefined) routes the
  // customer into the soft-booking flow. Providers whose flag is still undefined
  // (e.g. accounts that pre-date sync-stripe-status) need to log in once to populate
  // it; the sync runs automatically for every authenticated user on session start.
  const authorStripeVerified = authorPublicData?.stripeConnected === true;

  // A listing that already runs ON the soft-booking process has nowhere else to go, whatever the
  // provider's Stripe status: transactionProcessSoftBooking exposes only request-soft-booking and
  // accept-and-charge, so getRequestPaymentTransition finds nothing and the standard checkout
  // renders with no breakdown and a submit that cannot initiate.
  if (isSoftBookingProcess(processName)) {
    return true;
  }

  return isPaymentProcess && !authorStripeVerified;
};

/**
 * Structured booking-like payload for soft reservations.
 * Stored in transaction protectedData.
 */
export const createSoftReservationProtectedData = orderData => {
  const bookingDates = normalizeBookingDates(orderData);
  const setupDeadline = new Date(
    Date.now() + SOFT_RESERVATION_SETUP_DEADLINE_HOURS * 60 * 60 * 1000
  );

  return {
    softReservation: {
      status: SOFT_RESERVATION_STATUS_SETUP_PENDING,
      setupDeadline: setupDeadline.toISOString(),
      bookingStart: formatDateMaybe(bookingDates?.bookingStart),
      bookingEnd: formatDateMaybe(bookingDates?.bookingEnd),
      priceVariantName: orderData?.priceVariantName || null,
      quantity: Number.isInteger(orderData?.quantity) ? orderData.quantity : null,
      seats: Number.isInteger(orderData?.seats) ? orderData.seats : null,
      deliveryMethod: orderData?.deliveryMethod || null,
    },
  };
};
